import type { Vector3 } from "@pax/basemap";
import { type GlobeCamera, normalize } from "./globe";

/**
 * The world's axis, drawn as a skewer through the poles while they sweep across the view fast
 * enough to lose your bearings, as a flung globe makes them; after the Earth budget spike's viewer.
 *
 * Each pole gets a rod along the drawn surface's normal there, standing out of it and sunk into it by
 * the same length. On the globe both normals pass through the center, so the two rods are one
 * skewer; as the map unfurls they become spikes standing out of each pole. The north end is tipped
 * red, as a compass needle is. Rods are a few pixels wide on screen, and the ground hides what's
 * behind it, so the near end shows past the limb even while its pole is on the far side.
 */

/** How far a rod stands out of the ground: world radii, at most this share of the camera's altitude. */
const LENGTH_RADII = 0.45;
const LENGTH_OF_ALTITUDE = 0.6;
/** Half a rod's width, CSS pixels, and the share of its length its pointed tip takes. */
const HALF_WIDTH_PIXELS = 1.5;
const TIP = 0.12;
const ROD_COLOR = [0.36, 0.36, 0.4];
const NORTH_COLOR = [0.86, 0.24, 0.2];
/** Floats per vertex: the point, the far end it looks toward, its half width, and its color. */
const STRIDE = 10;

const VERTEX = `#version 300 es
in vec3 a_point;
in vec3 a_other;
in float a_halfWidth;
in vec3 a_color;
uniform mat4 u_viewProj;
uniform vec2 u_viewport;
out vec3 v_point;
out vec3 v_color;
void main() {
  vec4 here = u_viewProj * vec4(a_point, 1.0);
  vec4 there = u_viewProj * vec4(a_other, 1.0);
  // Across the rod on screen: perpendicular to its direction there, in pixels, then back to clip space.
  vec2 along = (there.xy / there.w - here.xy / here.w) * u_viewport;
  vec2 across = normalize(vec2(-along.y, along.x) + vec2(1e-9, 0.0));
  here.xy += across * a_halfWidth * 2.0 / u_viewport * here.w;
  here.z = 0.0;
  gl_Position = here;
  v_point = a_point;
  v_color = a_color;
}`;

// A rod's point is hidden when the line of sight to it meets the drawn sphere first.
const FRAGMENT = `#version 300 es
precision highp float;
uniform vec3 u_target;
uniform float u_altitude;
uniform float u_curvature;
uniform float u_opacity;
in vec3 v_point;
in vec3 v_color;
out vec4 outColor;
void main() {
  float h = u_altitude, k = u_curvature;
  vec3 eye = h * u_target;
  vec3 toward = v_point - eye;
  float reach = length(toward);
  vec3 d = toward / reach;
  float b = dot(d, u_target) * (1.0 + k * h);
  float c = h * (k * h + 2.0);
  float disc = b * b - k * c;
  if (b < 0.0 && disc >= 0.0 && c / (-b + sqrt(disc)) < reach * (1.0 - 1e-4)) discard;
  outColor = vec4(v_color * u_opacity, u_opacity);
}`;

export function createAxis(gl: WebGL2RenderingContext) {
  const program = gl.createProgram();
  for (const [type, source] of [
    [gl.VERTEX_SHADER, VERTEX],
    [gl.FRAGMENT_SHADER, FRAGMENT],
  ] as const) {
    const shader = gl.createShader(type);
    if (!shader) throw new Error("The axis's shader couldn't be made.");
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    gl.attachShader(program, shader);
  }
  gl.linkProgram(program);
  const buffer = gl.createBuffer();
  const layout = gl.createVertexArray();
  gl.bindVertexArray(layout);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  for (const [name, size, offset] of [
    ["a_point", 3, 0],
    ["a_other", 3, 3],
    ["a_halfWidth", 1, 6],
    ["a_color", 3, 7],
  ] as const) {
    const location = gl.getAttribLocation(program, name);
    if (location < 0) continue;
    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(location, size, gl.FLOAT, false, STRIDE * 4, offset * 4);
  }
  gl.bindVertexArray(null);
  const uniform = (name: string) => gl.getUniformLocation(program, name);

  return {
    draw(camera: GlobeCamera, opacity: number, size: { width: number; height: number }) {
      const vertices = rodVertices(camera);
      // biome-ignore lint/correctness/useHookAtTopLevel: WebGL's useProgram, not a React hook.
      gl.useProgram(program);
      gl.uniformMatrix4fv(uniform("u_viewProj"), false, new Float32Array(camera.viewProj));
      gl.uniform2f(uniform("u_viewport"), size.width, size.height);
      gl.uniform3fv(uniform("u_target"), camera.target);
      gl.uniform1f(uniform("u_altitude"), camera.altitude);
      gl.uniform1f(uniform("u_curvature"), camera.curvature);
      gl.uniform1f(uniform("u_opacity"), opacity);
      gl.bindVertexArray(layout);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STREAM_DRAW);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.drawArrays(gl.TRIANGLES, 0, vertices.length / STRIDE);
      gl.disable(gl.BLEND);
      gl.bindVertexArray(null);
    },
    dispose() {
      gl.deleteProgram(program);
      gl.deleteBuffer(buffer);
      gl.deleteVertexArray(layout);
    },
  };
}

function rodVertices(camera: GlobeCamera): Float32Array {
  const length = Math.min(LENGTH_RADII, LENGTH_OF_ALTITUDE * camera.altitude);
  const poles: Vector3[] = [
    [0, 0, 1],
    [0, 0, -1],
  ];
  const vertices: number[] = [];
  for (const [index, pole] of poles.entries()) {
    const base = camera.place(pole);
    // The drawn sphere k|a|² + 2a·T = 0 has its center at −T/k, so its outward normal at a is along T + k·a.
    const t = camera.target;
    const k = camera.curvature;
    const normal = normalize([t[0] + k * base[0], t[1] + k * base[1], t[2] + k * base[2]]);
    const at = (s: number): Vector3 => [
      base[0] + normal[0] * s * length,
      base[1] + normal[1] * s * length,
      base[2] + normal[2] * s * length,
    ];
    // From sunk to standing: the rod, then its tip narrowing to a point.
    const stations = [-1, 0.5, 0.5, 1 - TIP, 1];
    const widths = [HALF_WIDTH_PIXELS, HALF_WIDTH_PIXELS, HALF_WIDTH_PIXELS, HALF_WIDTH_PIXELS, 0];
    const strip: number[][] = [];
    stations.forEach((s, station) => {
      // Each vertex looks toward the far end of its rod, past it for the last.
      const other = at(s < 1 ? 1 : 2);
      // The north rod's standing half is red; the doubled station at 0.5 makes its edge sharp.
      const color = index === 0 && station >= 2 ? NORTH_COLOR : ROD_COLOR;
      for (const side of [-1, 1]) {
        strip.push([...at(s), ...other, side * (widths[station] ?? 0), ...color]);
      }
    });
    for (let corner = 0; corner + 2 < strip.length; corner++) {
      vertices.push(
        ...(strip[corner] ?? []),
        ...(strip[corner + 1] ?? []),
        ...(strip[corner + 2] ?? []),
      );
    }
  }
  return new Float32Array(vertices);
}
